import { useState } from "react";
import { ApiError, api, type Item, type TripView } from "./api.ts";
import { ConfirmDialog } from "./Dialog.tsx";
import { useOnline } from "./offline.ts";
import { REMOVE_FROM_TRIP, removeWords } from "./removing.ts";
import { Button } from "./souvenir.ts";
import { type Removed, useGone } from "./Todos.tsx";
import { ItemIcon } from "./ui.tsx";

// Remove from trip (DESIGN.md, Views, "Remove from trip"; features/map.md): last on every card in full, for anyone in
// the trip, a quiet button in coral with the bin. It always asks first, in a dialog that says what goes with it (a
// choice's options; booked, and its payments off Money: removing.ts), Cancel first and Remove in the usual primary
// style, since Updates can undo it. Removed, it's gone from every list at once and the card closes back onto where it
// was opened from, with Undo there (`onRemoved`, TripPage). Changed meanwhile, the dialog says so and shows it as it is
// now, for Remove again. Not on a to-do, which has its own Remove to-do, nor on a read-only trip (Details).
export function RemoveFromTrip({
  item,
  trip,
  options,
  onRemoved,
  onChanged,
}: {
  item: Item;
  trip: TripView;
  // A choice's options, which go with it.
  options: readonly Item[];
  onRemoved: (removed: Removed) => void;
  onChanged: () => void;
}) {
  const online = useOnline();
  const gone = useGone();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The version a refusal said it's at now, until the trip loads again with it.
  const [seen, setSeen] = useState(0);
  const version = Math.max(item.version, seen);
  const words = removeWords(item, item.type === "choice" ? options : []);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const { change, removed } = await api.removeItem(trip.id, item.id, version);
      setAsking(false);
      onRemoved({ id: item.id, title: item.title, change });
      gone(removed);
      onChanged();
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && e.status === 409) {
        const now = e.data["item"] as Item | undefined;
        if (now?.version) setSeen(now.version);
        setError("It changed while this was open, so nothing was removed. This is how it is now.");
        onChanged();
        return;
      }
      setError(e instanceof Error ? e.message : "Couldn't remove it. Try again.");
    }
  };
  return (
    <>
      <Button
        variant="quiet"
        size="sm"
        className="tm-start tm-remove-from-trip"
        data-edit="remove"
        disabled={!online}
        onClick={() => {
          setError(null);
          setBusy(false);
          setAsking(true);
        }}
      >
        <ItemIcon name="bin" size={20} />
        {REMOVE_FROM_TRIP}
      </Button>
      {asking && (
        <ConfirmDialog
          title={words.title}
          action="Remove"
          doing="Removing…"
          undoable
          busy={busy}
          error={error}
          onConfirm={remove}
          onCancel={() => setAsking(false)}
        >
          {words.lines.map((line) => (
            <p key={line} className="body">
              {line}
            </p>
          ))}
        </ConfirmDialog>
      )}
    </>
  );
}
